﻿import { PipingClassGenericView } from '@/apps/STID_Pipe/pipingClass.ts';
import { CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';

/**
 * Accepts a code and extracts a number value from its string attributes.
 */
function getAttributeValue(code: CodeEditFields, key: string) {
    const strValue = code[key];
    return strValue ? Number(strValue) : null;
}

/**
 * Tmin3 calculations
 */
function tmin_3_func(designPressureMaximum: number, outerDiameter: number, stress: number, nConvFactor: number, qualityFactor: number, yFactor: number): number {
    return designPressureMaximum * outerDiameter / (20 * stress * nConvFactor * qualityFactor + 2 * designPressureMaximum * yFactor);
}

/**
 * nConvFactor for the corresponding sUnit.
 * Currently only 'BARG' and 'PSI' is in use.
 */
function getNConvFactor(sUnit: string): number {
    switch (sUnit) {
        case 'BARG':
            return 1;
        case 'NMM2':
            return 6.895;/// NOTE: this seems to be incorrect, but value is not in use
        case 'KPA':
            return 100;
        case 'PSI':
            return 14.5038;
        default:
            return 1;
    }
}

/**
 * Gets the current sUnit (Pressure unit) for a Facility.
 *
 * This is a hardcoded list, and should be updated when we import PressureUnit from STID.
 * Currently there is only 5 US Facilities (Plants) that is using `PSI'.
 */
function getSUnit(scope: string): string {
    const psiScopes = ['TI', 'MRC', 'EFG', 'BAK', 'LAC'];
    if (psiScopes.some(psiScope => psiScope === scope)) {
        return 'PSI';
    } else {
        return 'BARG';
    }
}

/**
 * Tmin fields is a specific STID calculation.
 * This function returns a Object to be used in the CodeEditor.vue component as computed properties.
 *
 * More information can be found on the AB#123980
 */
export default function tminCalculations(scope: string, code: CodeEditFields, selectedPipingClass: PipingClassGenericView): PipingClassComputedValues | undefined {
    if (selectedPipingClass === null) return;
    const fractionDigits = 2;
    const sUnit = getSUnit(scope);
    const nConvFactor = getNConvFactor(sUnit);

    const designPressureMaximum = getAttributeValue(code, 'DesignPressureMaximum');
    const outerDiameter = getAttributeValue(code, 'OuterDiameter');
    const stress = getAttributeValue(code, 'Stress');
    const qualityFactor = getAttributeValue(code, 'QualityFactor');
    const yFactor = getAttributeValue(code, 'YFactor');
    const wallThickness = getAttributeValue(code, 'WallThickness');

    let tmin_3 = null;
    let tmin_2 = null;

    if (designPressureMaximum !== null && outerDiameter !== null && stress !== null && qualityFactor !== null && yFactor !== null) {
        tmin_3 = tmin_3_func(designPressureMaximum, outerDiameter, stress, nConvFactor, qualityFactor, yFactor)
            .toFixed(fractionDigits);

        tmin_2 = selectedPipingClass.Corrosion
            ? (tmin_3_func(designPressureMaximum, outerDiameter, stress, nConvFactor, qualityFactor, yFactor) + selectedPipingClass.Corrosion)
                .toFixed(fractionDigits)
            : 'Missing values';
    }

    const tmin_1 = wallThickness && selectedPipingClass.Corrosion
        ? (wallThickness - selectedPipingClass.Corrosion).toFixed(fractionDigits)
        : null;

    const in_diam = wallThickness && outerDiameter
        ? (-2 * wallThickness + outerDiameter).toFixed(fractionDigits)
        : null;

    return {
        inDiam: in_diam,
        sUnit: sUnit,
        tmin1: tmin_1,
        tmin2: tmin_2,
        tmin3: tmin_3
    };
}

export type PipingClassComputedValues = {
    tmin1: string | null;
    tmin2: string | null;
    tmin3: string | null;
    inDiam: string | null;
    sUnit: string;
};
